import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ClothingAnalysis {
  type: string;
  color: string;
  pattern: string;
  occasion: string;
  mood: string;
  brand: string | null;
  suggestedName: string;
  estimatedPrice: number;
  confidence: number;
}

export interface AnalysisResult {
  analysis: ClothingAnalysis | null;
  insufficientCredits?: boolean;
  required?: number;
  available?: number;
}

export function useClothingAnalysis() {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<ClothingAnalysis | null>(null);

  const analyzeImage = useCallback(async (imageFile: File): Promise<AnalysisResult> => {
    setAnalyzing(true);
    setAnalysis(null);

    try {
      // Convert file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
      });

      console.log('Sending image for analysis...');

      const { data, error } = await supabase.functions.invoke('analyze-clothing', {
        body: { imageBase64: base64 },
      });

      if (error) {
        console.error('Analysis error:', error);
        throw error;
      }

      // Handle insufficient credits
      if (data.error === 'insufficient_credits') {
        return {
          analysis: null,
          insufficientCredits: true,
          required: data.required,
          available: data.available,
        };
      }

      if (data.error && !data.type) {
        throw new Error(data.error);
      }

      console.log('Analysis result:', data);
      setAnalysis(data);
      
      if (data.confidence && data.confidence > 70) {
        toast.success(`Detected: ${data.suggestedName}`);
      } else {
        toast.info('AI analyzed your item - please verify details');
      }
      
      return { analysis: data };
    } catch (err) {
      console.error('Failed to analyze clothing:', err);
      toast.error('Could not analyze image. Please fill in details manually.');
      return { analysis: null };
    } finally {
      setAnalyzing(false);
    }
  }, []);

  const reset = useCallback(() => {
    setAnalysis(null);
    setAnalyzing(false);
  }, []);

  return {
    analyzing,
    analysis,
    analyzeImage,
    reset,
  };
}
